import { useParams } from 'react-router-dom';
import {
    limit, orderBy, query, where,
} from 'firebase/firestore';
import { RenderIf } from 'react-rainbow-components';
import {
    useRef, useEffect, useState, useCallback, useMemo,
} from 'react';
import { VirtuosoHandle, ListRange } from 'react-virtuoso';
import { showAppNotification } from '@rainbow-modules/app';
import useThread from 'data/firestore/agent/thread/use';
import useCustomer from 'data/firestore/agent/customer/use';
import useThreadMessages from 'data/firestore/agent/thread/message/useCollection';
import formatDate from 'components/SMSChat/ChatMessages/helpers/formatDate';
import { Customer } from 'data/firestore/agent/customer/types';
import { MetaAdReferral } from 'data/firestore/globals';
import useHttpMutation from 'data/firestore/useHttpMutation';
import { Thread } from 'data/firestore/agent/thread/types';
import { Message } from 'data/firestore/agent/thread/message/types';
import { EntityGet } from 'data/firestore/types';
import useSendThreadMessage from 'data/hooks/useSendThreadMessage';
import useResponderMapping from 'data/firestore/agent/respondermapping/useCollection';
import { getChannelFeatureName } from '../Settings/ChannelDeliverySection/helpers/getChannelFeatureName';
import CustomerInfo from './CustomerInfo';
import MessageBubble from './MessageBubble';
import DateBadge, { StyledDateBadge } from './dateBadge';
import MessageInput from './messageInput';
import AIStatus from './AIStatus';
import {
    Container,
    Header,
    Divider,
    MessagesContainer,
    StyledVirtuoso,
    LoadingContainer,
    LoadingMessage,
    ItemContainer,
    Space,
} from './styled';
import getCreatedBy from './helpers/getCreatedBy';

type ToolMessageEntity = EntityGet<Message> & { role: 'tool' };
type ToolMsgMap = Record<string, ToolMessageEntity>;

type GroupedMessage = EntityGet<Message> & {
    showGroupDate: boolean;
    dateCreated: Date;
};

const getVirtuosoItemContent = (
    agentId: string,
    customer: Customer,
    thread: Thread,
    toolMsgMap: ToolMsgMap,
    adMessageId?: string,
    adReferral?: MetaAdReferral | null,
// eslint-disable-next-line react/function-component-definition
) => (_index: number, message: GroupedMessage) => {
    const {
        id: messageId, showGroupDate, dateCreated, role,
    } = message;
    if (role === 'tool') return null;
    const createdBy = getCreatedBy({ agentId, message, customer: customer as Customer });
    const messageReferral = messageId === adMessageId ? adReferral : undefined;
    if (showGroupDate) {
        return (
            <ItemContainer key={messageId}>
                <Space />
                <DateBadge label={formatDate(dateCreated.toISOString())} />
                <Space />
                <MessageBubble
                    message={message}
                    createdBy={createdBy}
                    thread={thread}
                    toolMsgMap={toolMsgMap}
                    adReferral={messageReferral}
                />
            </ItemContainer>
        );
    }
    return (
        <MessageBubble
            key={messageId}
            message={message}
            createdBy={createdBy}
            thread={thread}
            toolMsgMap={toolMsgMap}
            adReferral={messageReferral}
        />
    );
};

const ThreadMessages = () => {
    const { agentId = '', threadId } = useParams<{ agentId: string; threadId: string }>();
    const virtuosoRef = useRef<VirtuosoHandle>(null);
    const [isScrolling, setScrolling] = useState(false);
    const [firstItemDate, setFirstItemDate] = useState<Date | null>();
    const messageInputRef = useRef<HTMLDivElement>(null);

    const {
        mutateAsync: sendMessage,
        isLoading: isSendingMessage = false,
    } = useSendThreadMessage({ agentId, threadId });

    const { mutateAsync: updateThread } = useHttpMutation({
        pathname: `/agents/${agentId}/threads/${threadId}`,
        method: 'PATCH',
    });

    const {
        data: thread,
        isLoading: isLoadingThread,
    } = useThread(agentId as string, threadId as string, {
        disabled: !agentId || !threadId,
    });

    const {
        data: messages = [],
        isLoading: isLoadingMessages,
    } = useThreadMessages(agentId as string, threadId as string, {
        disabled: !agentId || !threadId,
        listQueryFn: (ref) => query(ref, orderBy('createdAt', 'asc')),
    });

    const channel = thread?.from?.channel as string;
    const featureName = getChannelFeatureName(channel);

    const {
        data: responderMapping = [],
        isLoading: isLoadingResponderMapping,
    } = useResponderMapping(
        agentId,
        {
            listQueryFn: (ref) => query(ref, where('feature', '==', featureName), limit(1)),
            disabled: !agentId || !channel,
        },
    );

    const embeddedCustomer = thread?.customer as Customer | undefined;
    const customerId = embeddedCustomer?.id || '';
    const { data: customerRecord } = useCustomer(agentId, customerId, {
        disabled: !agentId || !customerId,
    });
    const customer = (customerRecord || embeddedCustomer) as Customer;
    const paidReferral = customerRecord?.metaAdReferral || thread?.metaAdReferral;
    const adMessageId = useMemo(() => {
        if (!paidReferral) return undefined;
        return messages.find((message) => message.role === 'user')?.id;
    }, [messages, paidReferral]);
    const isLoading = isLoadingThread || isLoadingMessages || isLoadingResponderMapping;

    const toolMsgMap = useMemo<ToolMsgMap>(
        () => messages.reduce<ToolMsgMap>((acc, message) => {
            if (message.role === 'tool') {
                acc[message.toolCallId] = message as ToolMessageEntity;
            }
            return acc;
        }, {}),
        [messages],
    );
    const isAiActive = thread?.aiResponseStatus !== 'disabled';
    const isManychatApi = !responderMapping?.length;

    // Group messages by date
    const groupedMessages = messages.map((message, index) => {
        const currentDate = new Date(message.createdAt);
        const previousMessage = index > 0 ? messages[index - 1] : null;
        const previousDate = previousMessage ? new Date(previousMessage.createdAt) : null;

        const showGroupDate = !previousDate
            || currentDate.toDateString() !== previousDate.toDateString();

        return {
            ...message,
            showGroupDate,
            dateCreated: currentDate,
        };
    });

    // Auto-scroll to bottom when messages change
    useEffect(() => {
        if (groupedMessages.length > 0 && !isLoading) {
            setTimeout(() => {
                virtuosoRef.current?.scrollToIndex({
                    index: groupedMessages.length - 1,
                    behavior: 'smooth',
                });
            }, 100);
        }
    }, [groupedMessages.length, isLoading]);

    const handleRangeChanged = useCallback(
        (range: ListRange) => setFirstItemDate(
            groupedMessages?.[range.startIndex]?.dateCreated,
        ),
        [groupedMessages],
    );

    const handleSendMessage = useCallback(
        async (text: string, files: File[]) => {
            try {
                await sendMessage({
                    message: text,
                    files,
                });
            } catch (error) {
                const message = (error as any)?.message || 'Something went wrong. Please try again.';
                showAppNotification({
                    title: 'Error',
                    description: message,
                    icon: 'error',
                    timeout: 5000,
                });
            }

            setTimeout(() => {
                messageInputRef.current?.focus();
            }, 0);

            if (isAiActive) {
                try {
                    updateThread({
                        body: {
                            aiResponseStatus: 'disabled',
                        },
                    });
                } catch (error) {
                    // no catch
                }
            }
        },
        [isAiActive, sendMessage, updateThread],
    );

    const handleToggleAIStatus = useCallback(
        async (isActive: boolean) => {
            try {
                await updateThread({
                    body: {
                        aiResponseStatus: isActive ? 'enabled' : 'disabled',
                    },
                });
                showAppNotification({
                    title: 'Success',
                    description: isActive ? 'Sara is now actively responding to messages.' : 'Sara will not be responding to messages.',
                    icon: 'success',
                    timeout: 5000,
                });
                return true;
            } catch (error) {
                const message = (error as any)?.message || 'Something went wrong. Please try again.';
                showAppNotification({
                    title: 'Error',
                    description: message,
                    icon: 'error',
                    timeout: 5000,
                });
            }
            return false;
        },
        [updateThread],
    );

    const { disableSendMessage, disableReason } = useMemo(() => {
        if (isLoading || isSendingMessage || !customer) {
            return {
                disableSendMessage: true,
                disableReason: '',
            };
        }

        if (isManychatApi) {
            const { manychat } = (customer.metadata || {}) as Record<string, unknown>;
            const { subscriberId } = (manychat || {}) as Record<string, string>;
            if (!subscriberId) {
                return {
                    disableSendMessage: true,
                    disableReason: 'You can\'t send messages to this customer right now. This will be fixed automatically when they message us again.',
                };
            }
        }

        if (channel === 'telegram') {
            return {
                disableSendMessage: false,
                disableReason: '',
            };
        }

        const latestMessage = messages.findLast((message) => message.role === 'user');
        if (!latestMessage) {
            return {
                disableSendMessage: true,
                disableReason: 'Cannot send message until customer initiates the conversation',
            };
        }
        const createdAt = new Date(latestMessage.createdAt);
        const diff = new Date().getTime() - createdAt.getTime();
        if (diff > 24 * 60 * 60 * 1000) {
            return {
                disableSendMessage: true,
                disableReason: 'Cannot send message after 24 hours since customer\'s last message',
            };
        }

        return {
            disableSendMessage: false,
            disableReason: '',
        };
    }, [isLoading, isSendingMessage, messages, customer, isManychatApi, channel]);

    return (
        <Container>
            <Header>
                <CustomerInfo
                    customer={customer}
                    channel={thread?.from?.channel}
                    paidReferral={paidReferral}
                />
                <Divider />
                <AIStatus isActive={isAiActive} onChange={handleToggleAIStatus} />
            </Header>
            <MessagesContainer>
                <RenderIf isTrue={isLoading}>
                    <LoadingContainer>
                        <LoadingMessage />
                        <LoadingMessage />
                        <LoadingMessage />
                    </LoadingContainer>
                </RenderIf>

                <RenderIf isTrue={!isLoading}>
                    <StyledVirtuoso
                        ref={virtuosoRef}
                        data={groupedMessages}
                        // eslint-disable-next-line max-len
                        itemContent={getVirtuosoItemContent(
                            agentId,
                            customer,
                            thread as Thread,
                            toolMsgMap,
                            adMessageId,
                            paidReferral,
                        )}
                        followOutput="smooth"
                        alignToBottom
                        initialTopMostItemIndex={groupedMessages.length - 1}
                        isScrolling={setScrolling}
                        rangeChanged={handleRangeChanged}
                    />
                </RenderIf>
                <StyledDateBadge
                    label={formatDate(firstItemDate?.toISOString())}
                    isScrolling={isScrolling}
                />
            </MessagesContainer>
            <MessageInput
                ref={messageInputRef}
                isManychatApi={isManychatApi}
                channel={thread?.from?.channel}
                disabled={disableSendMessage}
                reason={disableReason}
                onSend={handleSendMessage}
            />
        </Container>
    );
};

export default ThreadMessages;
